/* eslint-disable @typescript-eslint/no-misused-promises */
// node modules
import { Buffer } from 'buffer';
// modules
import Logger from 'modules/Logger';
import FormatService from 'modules/Format';
import ErrorService from 'modules/Error';
import AuthService from 'modules/Auth';
import NotificationService from 'modules/Notifications';
import AnalyticsService from 'modules/Analytics/service';
import TokenMapper from 'modules/TokenMapper';
import { FeedResponse, typeToJSON } from 'app/marketDataFeed';
// constants
import { EVENTS } from 'modules/Analytics/consts';
import { URL_AWS } from 'modules/WebSocket/modules/LiveV2/const';
import {
  ERROR_KEYS_MAP,
  WS_STATUS,
  RECONNECTION_DELAY,
  PING_MAX_COUNT,
  RECONNECTION_TIME_INTERVAL,
} from './constants';
// store
import BasicStore from './store';

export default class WsBasicService<Mes extends string> extends BasicStore {
  // connection
  public connection?: WebSocket;
  private subscriptions: WsSubscriptions<Mes | 'ws_connect'> = {};
  private socketURL: string;
  private feederKey: FeederUrlConfig['key'];
  private ignoreMessages: (Mes | BasicWsIncomingMessages)[] = ['pong'];

  private reconnectionTimer: number | null = null;
  private isReconnect = false;
  private connectionTime = 0;

  private marketDataPingInterval?: number;
  private marketDataPingCount = 0;

  private portfolioPingInterval?: number;
  private portfolioPingCount = 0;

  constructor(
    { key, URL: socketURL }: FeederUrlConfig, ignoreMessages: Mes[] = [],
  ) {
    super();
    this.feederKey = key;
    this.socketURL = `${socketURL}?requestId=${FormatService.requestId()}`;
    this.ignoreMessages = [...this.ignoreMessages, ...ignoreMessages];
  }

  private getMarketDataUrl() {
    return URL_AWS;
  }

  private cleanup() {
    // Clear WebSocket listeners
    if (this.connection) {
      this.connection.onopen = null;
      this.connection.onclose = null;
      this.connection.onmessage = null;
      this.connection.onerror = null;
    }

    // Clear intervals
    this.clearMarketDataPingInterval();
    this.clearPortfolioPingInterval();
  }

  public dispose = () => {
    if (this.reconnectionTimer) {
      clearTimeout(this.reconnectionTimer);
      this.reconnectionTimer = null;
    }

    this.cleanup();

    if (this.connection) {
      this.connection.close();
    }
  };

  connect = () => {
    const isConnectionActive =
      this.connection?.readyState === WS_STATUS.OPEN ||
      this.connection?.readyState === WS_STATUS.CONNECTING;

    if (!isConnectionActive) {
      if (this.feederKey === 'liveV2') {
        this.socketURL = `${this.getMarketDataUrl()}?requestId=${FormatService.requestId()}`;
      }
      // create connection
      this.connection = new WebSocket(this.socketURL);

      if (this.feederKey === 'liveV2') {
        this.connection.binaryType = 'arraybuffer';
      }

      this.connectionTime = Date.now();

      // register handlers
      this.connection.onopen = this.handleOpen;
      this.connection.onclose = this.handleClose;
      this.connection.onmessage = this.handleMessage;
      this.connection.onerror = this.handleError;
    }
  };

  // when connection is open
  private handleOpen = () => {
    Logger.log('WS', `connected to ${this.socketURL}`);

    this.notifySocketConnected();

    const source = this.getFeederName();

    const elapsedTime = Date.now() - this.connectionTime;

    AnalyticsService.sendEvent(
      EVENTS.WEB_SOCKET_CONNECTED,
      {
        Source: source,
        URL: this.getBaseUrl(),
        'Time taken': `${elapsedTime} ms`,
      },
      true,
    );

    this.connectionTime = 0;

    // reset all reconnection activities
    if (this.reconnectionTimer) {
      clearTimeout(this.reconnectionTimer);

      this.reconnectionTimer = null;
    }

    this.sendHeartBeatToReceiveSingleByte();

    // set success state
    this.setConnectionState(true, false);
    if (this.isReconnect) {
      this.setReconnectionStatus(true);
      this.isReconnect = false;
    }
    // remove errors
    ErrorService.clearError(ERROR_KEYS_MAP[this.feederKey]);
    NotificationService.deleteAlertByType('notice', 'WS_CONNECTION');
  };

  private sendHeartBeatToReceiveSingleByte = () => {
    let request:
      | MarketDataSubscribePingPongModeRequest
      | PortfolioSubscribePingPongModeRequest = {
      guid: FormatService.generateUUID(),
      method: 'heartbeat',
    };

    if (this.feederKey === 'portfolioV2') {
      request = {
        type: 'subscribe',
        messageType: 'heartbeat',
      };
    }

    this.send(request);
  };

  private getConnectionRequestIds = (event: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const closingConnectionParams = new URL(event?.target?.url).searchParams;
    const closingConnectionRequestId = closingConnectionParams.get('requestId');
    const connectionParams = new URL(this.connection?.url || '').searchParams;
    const connectionRequestId = connectionParams.get('requestId');

    return {
      connectionRequestId,
      closingConnectionRequestId,
    };
  };

  private clearMarketDataPingInterval = () => {
    this.marketDataPingCount = 0;
    if (this.marketDataPingInterval) {
      clearInterval(this.marketDataPingInterval);
    }
  };

  private clearPortfolioPingInterval = () => {
    this.portfolioPingCount = 0;
    if (this.portfolioPingInterval) {
      clearInterval(this.portfolioPingInterval);
    }
  };

  // connection is closed
  private handleClose = (event: any) => {
    const {
      connectionRequestId,
      closingConnectionRequestId,
    } = this.getConnectionRequestIds(event);

    if (closingConnectionRequestId === connectionRequestId) {
      Logger.log('WS', `disconnected from ${this.socketURL}.`, event);

      const source = this.getFeederName();

      if (this.feederKey === 'liveV2') {
        this.clearMarketDataPingInterval();
      } else {
        this.clearPortfolioPingInterval();
      }

      AnalyticsService.sendEvent(EVENTS.WEB_SOCKET_DISCONNECTED, {
        Source: source,
        URL: this.getBaseUrl(),
      });

      // set failure state
      this.setConnectionFailed();
      // if fails try to reconnect
      this.reconnect();
    }
  };

  // handle error
  private handleError = (event: Event) => {
    Logger.error(event);
  };

  // try to reconnect
  private reconnect = () => {
    this.isReconnect = true;
    // if reconnection timer is already set - ignore this attempt
    if (this.reconnectionTimer) return;

    // create timer
    this.reconnectionTimer = window.setTimeout(async () => {
      this.reconnectionTimer = null;
      // try to connect
      try {
        // check access token validity
        await AuthService.verifyToken();

        this.connect();
      } catch (e) {
        this.reconnect();
      }
    }, RECONNECTION_DELAY);
  };

  private handleSingleByteResponse = () => {
    this.clearMarketDataPingInterval();
    this.marketDataPingInterval = window.setInterval(() => {
      this.marketDataPingCount++;
      if (this.marketDataPingCount >= PING_MAX_COUNT) {
        this.disconnect();
        this.clearMarketDataPingInterval();
      }
    }, RECONNECTION_TIME_INTERVAL);
  };

  private handlePingResponse = () => {
    this.clearPortfolioPingInterval();
    this.portfolioPingInterval = window.setInterval(() => {
      this.portfolioPingCount++;
      if (this.portfolioPingCount >= PING_MAX_COUNT) {
        this.disconnect();
        this.clearPortfolioPingInterval();
      }
    }, RECONNECTION_TIME_INTERVAL);
  };

  // incoming messages
  private handleMessage = (rawData: MessageEvent) => {
    let response: WsResponse<Mes>;

    try {
      const currentWSTarget = rawData.currentTarget as WebSocket;

      if (currentWSTarget?.binaryType === 'arraybuffer') {
        const messageDataBuffer = Buffer.from(rawData.data);
        if (messageDataBuffer.byteLength === 1) {
          this.handleSingleByteResponse();
          return;
        }
        const parsedData = FeedResponse.decode(messageDataBuffer);
        response = {
          data: parsedData,
          error: null,
          response_type: typeToJSON(parsedData.type) as Mes,
          timestamp: 0,
        };
      } else {
        if (rawData.data === 'ping') {
          this.handlePingResponse();
          return;
        }
        response = JSON.parse(rawData.data);
      }

      // list of subscription for this event
      const subscriptions = this.subscriptions[response.response_type];

      if (subscriptions && subscriptions.length) {
        // execute each subscriber
        subscriptions.forEach(handler => {
          handler(response);
        });
      }
    } catch (error) {
      Logger.error(error);
    }
  };

  public send = <T = string | BaseObject>(data: T) => {
    let payload: string;
    let params: Uint8Array = new Uint8Array();

    if (typeof data === 'string') {
      payload = data;
    } else {
      payload = JSON.stringify(data);
    }

    if (this.feederKey === 'liveV2') {
      params = new Uint8Array(payload.length);
      for (let i = 0; i < payload.length; i++) {
        params[i] = payload.charCodeAt(i);
      }
    }

    if (this.connection?.readyState === 1) {
      this.connection.send(this.feederKey === 'liveV2' ? params : payload);
    } else {
      Logger.log(
        'WS',
        `you are trying to send message ${payload} before connection established`,
        this.socketURL,
      );
    }
  };

  public disconnect = () => {
    if (this.connection) {
      this.cleanup();
      this.connection.close();
    }

    // set failure state
    this.setConnectionFailed();

    // try reconnect
    this.reconnect();
  };

  protected notifySocketConnected = () => {
    const subscriptions = this.subscriptions.ws_connect;

    if (subscriptions) {
      subscriptions.forEach(handler =>
        handler({
          data: 'ws_connect',
          error: null,
          response_type: 'ws_connect',
          timestamp: Date.now(),
        }),
      );
    }
  };

  protected setConnectionFailed = () => {
    this.setConnectionState(false, true);
    this.setReconnectionStatus(false);
    if (this.feederKey === 'liveV2') {
      TokenMapper.resetTokenMapper();
    }

    ErrorService.handleError(
      ERROR_KEYS_MAP[this.feederKey],
      'Unhandled disconnect',
    );
  };

  // register handlers
  public registerHandler = (key: Mes | 'ws_connect', handler: WsHandler) => {
    if (!this.subscriptions[key]) {
      this.subscriptions[key] = [];
    }

    this.subscriptions[key]!.push(handler);
  };

  private getFeederName() {
    return this.feederKey === 'liveV2'
      ? 'Market Data Feeder'
      : 'Portfolio Feeder';
  }

  private getBaseUrl = () => {
    const [baseUrl] = this?.connection?.url?.split('?') || [];
    return baseUrl;
  };
}
